import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { usePatientLive } from '../../lib/patientLive.js'
import {
  Plus, ChevronDown, ChevronRight,
  Trash2, Stethoscope, CheckCircle2, Gift,
} from 'lucide-react'
import { apiGet, postJson, apiDelete } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorState, Loading, PageHeader, fmtDate, useToast,
} from '../../kit'
import PromotionPicker from '../../components/promotion/PromotionPicker.jsx'
import {
  asPackages, packageUsed, packageN, packageState, packageProgress, canRemovePackage,
  promoName, promoDetail, promoMaintype, promoSubtype, packageSeller, packageVisits,
  packageSteps, stepPriceLabel, comparePackages, packageMaxMismatch, isPackageDead,
} from '../../lib/patientPromotion.js'

// ── /patient/:id/promotion — โปรโมชั่น ────────────────────────────────────────
//
// "What does this patient still have coming to them?" — a LOOKUP, not a
// workstation. Redemption (POST /promotion_patient/add) deliberately lives with
// the visit: it needs a treatment_id this page cannot know, creates a billing row,
// re-runs autofeeBilling, fires two realtime channels and mints a commission row.
// This page shows the balance and its provenance, and sells another package.
//
// ⚠️ LAYOUT NOTE — one flat list, on purpose. Sampled across the 500 newest active
// promotion_patient rows: 341 distinct patients, 277 of them (81 %) hold exactly
// ONE package, median 1. Section headers + Maintype groups + collapsible cards
// would wrap four levels of chrome around a single row for four patients in five.
//
// Money is deliberately absent: the only figures on the payload (treatment[].price*)
// are the WHOLE VISIT's totals from a billing_price addon, not this package's share,
// and labelling them as package value would be a lie.

const SPLIT_THRESHOLD = 4   // only bother splitting used-up packages out above this

export default function PatientPromotion() {
  const { id } = useParams()
  // `liveEvt`, not `live` — this page already calls its unfinished packages `live`.
  const { patient, live: liveEvt } = useOutletContext() || {}

  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [failed, setFailed] = useState(false)
  const [expanded, setExpanded] = useState(() => new Set())
  const [picking, setPicking] = useState(false)
  const [confirmDel, setConfirmDel] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [showDone, setShowDone] = useState(false)
  const toast = useToast()

  // Guards against a slow patient-1 response landing under patient 2's header —
  // this page shows purchasable balances, so a mismatch is worse than a flicker.
  const aliveId = useRef(id)
  useEffect(() => { aliveId.current = id }, [id])

  const load = useCallback(() => {
    if (!id) return
    setError(null)
    return apiGet('/api/promotion-patient', { patient_id: id })
      .then((r) => { if (aliveId.current !== id) return; setRows(asPackages(r)); setFailed(false) })
      .catch((e) => {
        if (aliveId.current !== id) return
        setError(apiErrorText(e, 'โหลดโปรโมชั่นไม่สำเร็จ'))
        // Never [] on failure: "ยังไม่มีโปรโมชั่น" beside an error banner tells the
        // counter this patient bought nothing, which is not what we know.
        setRows([]); setFailed(true)
      })
  }, [id])

  // Same-component-across-patients reset: `picking` in particular would otherwise
  // leave the catalog modal open and re-point it at the new patient mid-selection.
  useEffect(() => {
    setRows(null); setExpanded(new Set()); setFailed(false)
    setConfirmDel(null); setPicking(false); setBusyId(null); setShowDone(false)
    load()
  }, [load])

  // Live: a package sold or a step consumed (promotion_patient), and billing —
  // drawing on a package writes the billing line that moves the balance shown here.
  // `load()` alone, so the catalog modal and expanded cards survive the refresh.
  usePatientLive(liveEvt, ['promotion', 'billing'], () => { load() })

  const sorted = useMemo(() => (rows || []).slice().sort(comparePackages), [rows])

  // What the patient already holds, so the picker can mark it. `heldUnused` is the
  // dangerous subset — a second copy of a package they have never drawn on is almost
  // always a mistake, and promotion_patient has no duplicate guard upstream.
  const held = useMemo(
    () => (rows || []).map((r) => Number(r.promotion_id)).filter(Boolean), [rows])
  const heldUnused = useMemo(
    () => (rows || []).filter((r) => packageUsed(r) === 0)
      .map((r) => Number(r.promotion_id)).filter(Boolean), [rows])
  const { live, done } = useMemo(() => {
    const live = [], done = []
    for (const r of sorted) (packageState(r) === 'done' ? done : live).push(r)
    return { live, done }
  }, [sorted])

  // The used-up pile only gets its own collapsed section once there is enough of a
  // list for it to be in the way. Below that it stays inline, dimmed.
  const splitDone = sorted.length > SPLIT_THRESHOLD && done.length > 0
  const visible = splitDone ? live : sorted

  // Sequential, and the boundary is reported. Each POST is its own promotion_patient
  // row, so a failure at #3 leaves #1–2 genuinely created — throwing a bare error
  // would tell the user "it failed" while two packages silently exist. Same rule as
  // the stock cart: partial success is durable, so it has to be visible.
  async function assign(promotionIds) {
    const done = []
    try {
      for (const pid of promotionIds) {
        await postJson('/api/promotion-patient', { patient_id: Number(id), promotion_id: Number(pid) })
        done.push(Number(pid))
      }
      setPicking(false)
      toast.success('เพิ่มโปรโมชั่นแล้ว')
    } catch (e) {
      const msg = done.length
        ? `เพิ่มได้ ${done.length} จาก ${promotionIds.length} รายการ แล้วเกิดข้อผิดพลาด: ${apiErrorText(e)}`
        : apiErrorText(e, 'เพิ่มโปรโมชั่นไม่สำเร็จ')
      // Record it on the PAGE as well as rethrowing to the picker. The picker shows it
      // inline while it is open, but it is a modal — if it ever unmounts mid-write the
      // inline copy dies with it, and a partial success with no visible error reads as
      // "nothing happened", which invites re-sending rows that already landed.
      setError(msg)
      toast.error('เพิ่มโปรโมชั่นไม่สำเร็จ', { detail: msg })
      const err = new Error(msg)
      err.done = done          // the picker un-ticks these so a retry can't re-send them
      throw err
    } finally {
      // Always — the rows that DID land are durable and must become visible, or the
      // only affordance left is "เพิ่มให้คนไข้" again, which re-POSTs them.
      if (done.length) await load()
    }
  }

  async function remove(pkg) {
    setBusyId(pkg.id)
    try {
      await apiDelete(`/api/promotion-patient/${pkg.id}?patient_id=${encodeURIComponent(id)}`)
      setConfirmDel(null)
      toast.success('ลบแพ็กเกจแล้ว')
      await load()
    } catch (e) {
      setError(apiErrorText(e))
      toast.error('ลบแพ็กเกจไม่สำเร็จ', { detail: apiErrorText(e) })
      setConfirmDel(null)
    } finally { setBusyId(null) }
  }

  const toggle = (pid) => setExpanded((s) => {
    const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid); return n
  })

  if (rows === null) {
    return (
      <div>
        <Title count={null} />
        <Loading label="กำลังโหลดโปรโมชั่น…" />
      </div>
    )
  }

  return (
    <div>
      <Title
        count={failed ? null : rows.length}
        action={!failed && (
          <Button icon={Plus} onClick={() => setPicking(true)}>เพิ่มโปรโมชั่นให้คนไข้</Button>
        )}
      />

      {/* One slot for both a failed load and a failed add / remove — the message says which. */}
      {error && (
        <ErrorState
          title={failed ? 'โหลดข้อมูลไม่สำเร็จ' : 'ทำรายการไม่สำเร็จ'}
          message={error}
          onRetry={failed ? load : undefined}
          className="mb-5"
        />
      )}

      {!failed && (
        <>
          {visible.length === 0 && sorted.length > 0 ? null : sorted.length === 0 ? (
            <EmptyState icon={Gift} title="ยังไม่มีโปรโมชั่นของคนไข้รายนี้" />
          ) : (
            <div className="space-y-2.5">
              {visible.map((p) => (
                <PackageCard
                  key={p.id}
                  pkg={p}
                  open={expanded.has(p.id)}
                  busy={busyId === p.id}
                  onToggle={() => toggle(p.id)}
                  onRemove={() => setConfirmDel(p)}
                />
              ))}
            </div>
          )}

          {splitDone && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowDone((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 hover:text-slate-700"
              >
                {showDone ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                ใช้ครบแล้ว ({done.length})
              </button>
              {showDone && (
                <div className="space-y-2.5 mt-2.5">
                  {done.map((p) => (
                    <PackageCard
                      key={p.id}
                      pkg={p}
                      open={expanded.has(p.id)}
                      busy={busyId === p.id}
                      onToggle={() => toggle(p.id)}
                      onRemove={() => setConfirmDel(p)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {picking && (
        <PromotionPicker
          patientId={Number(id)}
          patientName={patientLabel(patient)}
          held={held}
          heldUnused={heldUnused}
          title="เพิ่มโปรโมชั่นให้คนไข้"
          subtitle={patientLabel(patient)}
          confirmLabel="เพิ่มให้คนไข้"
          onConfirm={assign}
          onClose={() => setPicking(false)}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          tone="danger"
          title="ลบแพ็กเกจนี้?"
          message={`${promoName(confirmDel)} — ยังไม่ได้ใช้สิทธิ์ การลบจะซ่อนแพ็กเกจนี้ออกจากคนไข้ (ยังเก็บไว้ในระบบ)`}
          confirmLabel="ลบแพ็กเกจ"
          busy={!!busyId}
          onConfirm={() => remove(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */

// `action` sits on the title row so "add" is reachable without scrolling past
// every package (it used to be at the very bottom, under ใช้ครบแล้ว).
function Title({ count, action }) {
  return (
    <PageHeader
      level={2}
      title="โปรโมชั่น"
      meta={count != null ? `${count} แพ็กเกจ` : null}
      actions={action || null}
      className="mb-5"
    />
  )
}

const BAR_TONE = {
  unused: 'bg-slate-300',
  active: 'bg-brand-500',
  open:   'bg-slate-300',
  done:   'bg-emerald-500',
  over:   'bg-rose-500',
  dead:   'bg-rose-300',
}

function PackageCard({ pkg, open, busy, onToggle, onRemove }) {
  const state = packageState(pkg)
  const n = packageUsed(pkg)
  const N = packageN(pkg)
  const pct = packageProgress(pkg)
  const removable = canRemovePackage(pkg)
  const steps = packageSteps(pkg)
  const visits = packageVisits(pkg)
  const detail = promoDetail(pkg)
  const finished = state === 'done'
  const dead = isPackageDead(pkg)
  // On a dead package the mismatch chip's own tooltip ("ใช้ค่าจากแพตเทิร์น") would be
  // wrong — there is no usable value in the pattern. The rose chip replaces it.
  const mismatch = dead ? null : packageMaxMismatch(pkg)

  return (
    <div className={`rounded-2xl border bg-white transition ${finished ? 'border-dashed border-slate-200 opacity-60' : 'border-slate-200/80'}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full text-left px-4 py-3 flex items-start gap-3"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14.5px] font-medium text-slate-900">{promoName(pkg)}</span>
            {finished && <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />}
            {mismatch && (
              <Badge
                tone="amber"
                className="shrink-0"
                title={`แคตตาล็อกบันทึก max = ${mismatch.stored} แต่แพตเทิร์นรวมได้ ${mismatch.computed} — ใช้ค่าจากแพตเทิร์น`}
              >
                ตัวเลขไม่ตรงกัน
              </Badge>
            )}
            {dead && (
              <Badge
                tone="rose"
                className="shrink-0"
                title="แพตเทิร์นของโปรโมชั่นนี้เริ่มที่ repeat_acc = 0 ระบบคิดเงินจึงเลือกขั้นไหนไม่ได้เลย — ต้องแก้ที่ ตั้งค่าโปรโมชั่น ก่อนจึงจะใช้ได้"
              >
                ใช้สิทธิ์ไม่ได้
              </Badge>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span className="h-1.5 flex-1 max-w-[200px] rounded-full bg-slate-100 overflow-hidden">
              <span className={`block h-full rounded-full ${BAR_TONE[state]}`} style={{ width: `${Math.round(pct * 100)}%` }} />
            </span>
            <span className={`text-[13px] font-semibold tabular-nums ${state === 'over' || dead ? 'text-rose-600' : 'text-slate-700'}`}>
              {/* No denominator when the catalog has none — "6/0" would be nonsense.
                  A dead package gets NO number at all: any "n / N" here reads as a
                  balance the counter can promise, and nothing will ever bill. */}
              {dead ? 'ตั้งค่าไม่ถูกต้อง' : N ? `${n} / ${N}` : `${n} ครั้ง`}
            </span>
          </div>
          <div className="mt-1 text-[12.5px] text-slate-400 truncate" title={detail}>
            {[promoMaintype(pkg), promoSubtype(pkg), detail.split('\n')[0], packageSeller(pkg) && `ขายโดย ${packageSeller(pkg)}`,
              pkg.Creation_Date ? fmtDate(pkg.Creation_Date) : '']
              .filter(Boolean).join(' · ')}
          </div>
        </div>
        <span className="shrink-0 text-slate-300 mt-0.5">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-3.5 pt-0.5 border-t border-slate-100 space-y-3.5">
          {detail && (
            <p className="text-[12.5px] text-slate-500 whitespace-pre-wrap pt-3">{detail}</p>
          )}

          <Block label="สิ่งที่อยู่ในแพ็กเกจ">
            {steps.length === 0 ? (
              <p className="text-[12.5px] text-slate-400">ไม่มีข้อมูลรายละเอียดแพ็กเกจ</p>
            ) : (
              <ul className="space-y-1">
                {steps.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-[13px] text-slate-600">
                    <span className="text-slate-400 tabular-nums w-[52px] shrink-0">ขั้น {i + 1}</span>
                    <span className="tabular-nums font-medium text-slate-700 w-[44px] shrink-0">×{s.repeat}</span>
                    <span className="tabular-nums">{stepPriceLabel(s.price)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Block>

          <Block label={`ครั้งที่มารักษา (${visits.length})`}>
            {visits.length === 0 ? (
              // count > 0 with no visible visit is NOT "unused" — the visits addon
              // filters treatment.active while the counter does not, so this is a
              // redemption charged against a voided visit. Say that, don't launder it.
              <p className={`text-[12.5px] ${n > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                {n > 0
                  ? `ใช้สิทธิ์ไปแล้ว ${n} ครั้ง แต่ไม่พบการรักษาที่เกี่ยวข้อง — การรักษาอาจถูกยกเลิก`
                  : 'ยังไม่ได้ใช้สิทธิ์'}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {visits.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))).map((v) => (
                  <li key={v.id} className="flex items-center gap-2 text-[13px]">
                    <span className="tabular-nums text-slate-500 w-[84px] shrink-0">{fmtDate(v.date)}</span>
                    <span className="inline-flex items-center gap-1 text-slate-600 shrink-0">
                      {v.profile_img
                        ? <img src={v.profile_img} alt="" className="w-4 h-4 rounded-full object-cover bg-slate-100" />
                        : <Stethoscope size={11} className="text-slate-400" />}
                      {/* `code` on a treatment row is the DOCTOR's, flattened from a Doctor
                          addon — not the patient's, despite living on `treatment`. */}
                      {v.code || `หมอ #${v.doctor_id}`}
                    </span>
                    {v.detail && <span className="text-slate-400 truncate">{v.detail}</span>}
                  </li>
                ))}
              </ul>
            )}
            {n !== visits.length && (
              // The two numbers legitimately differ two ways, and the payload cannot
              // tell them apart: several uses redeemed on ONE visit, or a visit that
              // was voided while its use still burns the counter. Say both.
              <p className="mt-1.5 text-[12px] text-slate-400 leading-relaxed">
                ใช้สิทธิ์ {n} ครั้ง แต่มีการรักษา {visits.length} ครั้ง — อาจใช้หลายสิทธิ์ในการรักษาครั้งเดียว
                หรือการรักษาบางครั้งถูกยกเลิกไปแล้ว
              </p>
            )}
          </Block>

          <div className="pt-0.5">
            {removable ? (
              <Button variant="danger-soft" size="sm" icon={Trash2} onClick={onRemove} loading={busy}>ลบแพ็กเกจนี้</Button>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-400" title="แก้ที่รายการเรียกเก็บเงินของการรักษาแทน">
                <Trash2 size={13} /> ใช้สิทธิ์ไปแล้ว — ลบไม่ได้
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function Block({ label, children }) {
  return (
    <div>
      <div className="text-[11.5px] font-semibold uppercase tracking-[0.1em] text-slate-400 mb-1.5">{label}</div>
      {children}
    </div>
  )
}

const patientLabel = (p) =>
  [p?.prefix, p?.name, p?.surname].filter(Boolean).join(' ').trim() || p?.fullname || (p?.id ? `#${p.id}` : '')

